import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { objectivesToolsListOpened, previewObjectivesAction, type AnalyticsEvent } from '../../core/events';
import { createAnalytics, nullAnalytics, stopAnalytics, type Analytics } from '../analytics/client';
import ObjectivesHeader from './ObjectivesHeader';
import ObjectivesWelcome, { focusOwnPlan } from './ObjectivesWelcome';

/** A local, static sample. Authenticated processing remains in ObjectivesApp. */
export default function PublicObjectives({ signIn, analytics: injected }: { signIn: ReactNode; analytics?: Analytics }) {
  const ownPlanRef = useRef<HTMLDivElement>(null);
  const [analytics, setAnalytics] = useState<Analytics | null>(injected ?? null);
  const viewed = useRef(false);
  const track = useCallback((event: AnalyticsEvent) => {
    try { analytics?.track(event); } catch (error) { console.warn('[analytics] track failed', error); }
  }, [analytics]);

  useEffect(() => {
    if (injected) return;
    let live = true;
    // No identity or token: explicit events only, without session recording or profile calls.
    createAnalytics().then((value) => live && setAnalytics(value)).catch(() => live && setAnalytics(nullAnalytics()));
    // Preserve the anonymous ID so identify() can connect sample exploration to sign-in.
    return () => { live = false; stopAnalytics({ resetIdentity: false }); };
  }, [injected]);

  useEffect(() => {
    if (!analytics || viewed.current) return;
    viewed.current = true;
    track(previewObjectivesAction('welcome_viewed', { surface: 'signed_out' }));
  }, [analytics, track]);

  return (
    <>
      <ObjectivesHeader onToolsListOpened={() => track(objectivesToolsListOpened())} />
      <main className="page">
        <ObjectivesWelcome surface="signed_out" onTrack={track} onUseOwnPlan={() => focusOwnPlan(ownPlanRef.current)} />
        <div ref={ownPlanRef} tabIndex={-1} className="own-plan sign-in-section">
          <div><p className="eyebrow">Bring your own material</p><h2>Ready to check something you found?</h2><p className="hint">Sign in, choose what you teach, and upload the lesson or material you want to check.</p></div>
          <div className="sign-in-control ph-no-capture">{signIn}</div>
        </div>
      </main>
    </>
  );
}
